<template>
  <div class="comparison-radar">
    <canvas ref="graph"></canvas>
  </div>
</template>

<script>
export default {
  name: "ComparisonRadarGraph",
  props: {
    datasets: {
      type: Array,
      required: true,
      // Expected format: [{ data: [val1, val2, val3], color: '#color', label: 'Company Name' }]
    },
    width: {
      type: Number,
      default: 600,
    },
    height: {
      type: Number,
      default: 600,
    },
    legend: {
      type: Array,
      required: true,
    },
    numberOfDataPoints: {
      type: Number,
      default: 3,
    },
  },
  data() {
    return {
      context: null,
    };
  },
  mounted() {
    this.$refs["graph"].height = this.height;
    this.$refs["graph"].width = this.width;
    this.context = this.$refs["graph"].getContext("2d");
    this.draw();
  },
  watch: {
    datasets: {
      handler() {
        if (this.context) {
          this.draw();
        }
      },
      deep: true,
    },
  },
  methods: {
    draw() {
      if (!this.context) return;
      const ctx = this.context;
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      this.drawRadarGraph();
    },
    drawRadarGraph() {
      const ctx = this.context;
      let angle = -1.5715 + (6.286 / this.numberOfDataPoints / 2);
      
      // Draw lines and labels
      for (let i = 0; i < this.numberOfDataPoints; i++) {
        this.lineAtAngle(ctx.canvas.width / 2, ctx.canvas.height / 2, 260 / 600 * ctx.canvas.height, angle);
        this.drawNumber(i, angle);
        angle += 6.286 / this.numberOfDataPoints;
      }
      
      this.drawMiddleCircle();
      
      // Draw concentric circles
      let radius = 50 / 600 * ctx.canvas.height + 20 / 600 * ctx.canvas.height;
      for (let i = 0; i < 10; i++) {
        this.drawOuter(radius);
        radius += 20 / 600 * ctx.canvas.height;
      }
      
      // Draw all datasets
      this.drawAllDatasets();
    },
    lineAtAngle(x1, y1, length, angle) {
      const ctx = this.context;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.strokeStyle = "#BBBBBB";
      ctx.lineTo(x1 + length * Math.cos(angle), y1 + length * Math.sin(angle));
      ctx.stroke();
    },
    drawMiddleCircle() {
      const ctx = this.context;
      ctx.beginPath();
      ctx.arc(ctx.canvas.width / 2, ctx.canvas.height / 2, 50 / 600 * ctx.canvas.height, 0, 2 * Math.PI, false);
      ctx.fillStyle = "white";
      ctx.fill();
      ctx.lineWidth = 1;
      ctx.strokeStyle = "#BBBBBB";
      ctx.stroke();
    },
    drawOuter(radius) {
      const ctx = this.context;
      ctx.beginPath();
      ctx.arc(ctx.canvas.width / 2, ctx.canvas.height / 2, radius, 0, 2 * Math.PI, false);
      ctx.lineWidth = 1;
      ctx.strokeStyle = "#BBBBBB";
      ctx.stroke();
    },
    drawNumber(i, angle) {
      const ctx = this.context;
      ctx.beginPath();
      ctx.font = (20 / 600 * ctx.canvas.width) + "px Quicksand";
      ctx.fillStyle = "black";
      let x = ctx.canvas.width / 2 + 270 / 600 * ctx.canvas.width * Math.cos(angle);
      let y = ctx.canvas.height / 2 + 270 / 600 * ctx.canvas.height * Math.sin(angle);
      let legendIndex = i % this.legend.length;
      let legendText = this.legend[legendIndex];

      if (legendText === "Experience") {
        ctx.fillText(legendText, x-35, y+10);
      } else {
        ctx.fillText(legendText, x - 45, y);
      }
    },
    drawAllDatasets() {
      if (!this.datasets || this.datasets.length === 0) return;
      
      // Draw each dataset
      this.datasets.forEach((dataset) => {
        if (dataset.data && dataset.data.length > 0) {
          this.drawDataset(dataset.data, dataset.color);
        }
      });
    },
    drawDataset(data, color) {
      if (!data || data.length === 0) return;
      

      let angle = -1.5715 + (6.286 / this.numberOfDataPoints / 2);
      
      // Get color with alpha for fill
      const fillColor = this.hexToRgba(color, 0.2);
      
      for (let i = 0; i < data.length && i < this.numberOfDataPoints; i++) {
        this.drawPoint(data[i], angle, color);
        
        if (i > 0) {
          this.drawConnector(
            data[i - 1],
            angle - 6.286 / this.numberOfDataPoints,
            data[i],
            angle,
            color
          );
          this.drawPolygon(
            data[i - 1],
            angle - 6.286 / this.numberOfDataPoints,
            data[i],
            angle,
            fillColor
          );
        }
        
        if (i === this.numberOfDataPoints - 1) {
          this.drawConnector(
            data[i],
            angle,
            data[0],
            -1.5715 + (6.286 / this.numberOfDataPoints / 2),
            color
          );
          this.drawPolygon(
            data[i],
            angle,
            data[0],
            -1.5715 + (6.286 / this.numberOfDataPoints / 2),
            fillColor
          );
        }
        
        angle += 6.286 / this.numberOfDataPoints;
      }
    },
    drawPoint(value, angle, color) {
      const ctx = this.context;
      let x = ctx.canvas.width / 2 + (50 / 600 * ctx.canvas.width + 20 / 600 * ctx.canvas.width * value) * Math.cos(angle);
      let y = ctx.canvas.height / 2 + (50 / 600 * ctx.canvas.height + 20 / 600 * ctx.canvas.height * value) * Math.sin(angle);
      ctx.beginPath();
      ctx.rect(x - 10 / 600 * ctx.canvas.width, y - 10 / 600 * ctx.canvas.height, 20 / 600 * ctx.canvas.width, 20 / 600 * ctx.canvas.height);
      ctx.fillStyle = color;
      ctx.fill();
    },
    drawConnector(value1, angle1, value2, angle2, color) {
      const ctx = this.context;
      let x = ctx.canvas.width / 2 + (50 / 600 * ctx.canvas.width + 20 / 600 * ctx.canvas.width * value1) * Math.cos(angle1);
      let y = ctx.canvas.height / 2 + (50 / 600 * ctx.canvas.height + 20 / 600 * ctx.canvas.height * value1) * Math.sin(angle1);
      let x1 = ctx.canvas.width / 2 + (50 / 600 * ctx.canvas.width + 20 / 600 * ctx.canvas.width * value2) * Math.cos(angle2);
      let y1 = ctx.canvas.height / 2 + (50 / 600 * ctx.canvas.height + 20 / 600 * ctx.canvas.height * value2) * Math.sin(angle2);
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x1, y1);
      ctx.lineWidth = 2;
      ctx.strokeStyle = color;
      ctx.stroke();
    },
    drawPolygon(value1, angle1, value2, angle2, color) {
      const ctx = this.context;
      let x = ctx.canvas.width / 2 + (50 / 600 * ctx.canvas.width + 20 / 600 * ctx.canvas.width * value1) * Math.cos(angle1);
      let y = ctx.canvas.height / 2 + (50 / 600 * ctx.canvas.height + 20 / 600 * ctx.canvas.height * value1) * Math.sin(angle1);
      let x1 = ctx.canvas.width / 2 + (50 / 600 * ctx.canvas.width + 20 / 600 * ctx.canvas.width * value2) * Math.cos(angle2);
      let y1 = ctx.canvas.height / 2 + (50 / 600 * ctx.canvas.height + 20 / 600 * ctx.canvas.height * value2) * Math.sin(angle2);
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x1, y1);
      ctx.lineTo(ctx.canvas.width / 2, ctx.canvas.height / 2);
      ctx.lineWidth = 2;
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
    },
    hexToRgba(hex, alpha) {
      // Remove # if present
      hex = hex.replace('#', '');
      
      // Parse hex values
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
  },
};
</script>

<style scoped>
.comparison-radar {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
